import {ReactNode} from 'react';

import {ProductGroup} from 'core/constants/productGroups';
import {
    CheckStatus,
    DocumentReturnSource,
    DocumentReturnType,
    DocumentStatus,
    DocumentReportType,
    ProductStatus,
} from 'core/dictionaries';

import {DisaggregationType} from './disaggregation';
import {PackageTypes, PackagingType} from './gtins';
import {
    BlockingFormationProcess,
    BlockingScopes,
    BlockingSourceFormation,
    BlokcingReason,
} from './products-blocking';

export enum DocumentsAccessors {
    id = 'reportId',
    irn = 'irn',
    createdDate = 'createdAt',
    owner = 'owner',
    seller = 'seller',
    buyer = 'buyer',
    type = 'type',
    status = 'status',
    side = 'side',
    sellerLocation = 'sellerLocation',
    buyerLocation = 'buyerLocation',
}

export interface DocumentsGridRawRow {
    irn: string;
    reportId: string;
    createdAt: string;
    type: DocumentReportType;
    owner: string;
    seller: string | null;
    buyer: string | null;
    status: DocumentStatus;
    sellerLocation: string;
    buyerLocation: string;
}

export interface DocumentsGridFormattedRow {
    [DocumentsAccessors.id]: string;
    [DocumentsAccessors.createdDate]: string;
    [DocumentsAccessors.owner]?: string;
    [DocumentsAccessors.seller]?: string;
    [DocumentsAccessors.buyer]?: string;
    [DocumentsAccessors.type]?: number;
    [DocumentsAccessors.status]: string;
    [DocumentsAccessors.sellerLocation]?: string;
    [DocumentsAccessors.buyerLocation]?: string;
}

export enum MovingAccessors {
    id = 'reportId',
    createdDate = 'createdAt',
    status = 'status',
    employeeFullName = 'employeeFullName',
    storageLocationFrom = 'storageLocationFrom',
    storageLocationTo = 'storageLocationTo',
    tin = 'tin',
}

export interface MovingGridRawRow {
    reportId: string;
    createdAt: string;
    status: DocumentStatus;
    employeeFullName: string;
    storageLocationFrom: string;
    storageLocationTo: string;
    tin?: string;
}

export interface MovingGridFormattedRow {
    [MovingAccessors.id]: string;
    [MovingAccessors.createdDate]: string;
    [MovingAccessors.status]: ReactNode | string | null;
    [MovingAccessors.storageLocationFrom]?: string | null;
    [MovingAccessors.storageLocationTo]?: string | null;
    [MovingAccessors.tin]?: string;
}

export enum WithdrawalAccessors {
    id = 'reportId',
    createdDate = 'createdAt',
    status = 'status',
    reason = 'reason',
    codesCount = 'codesCount',
    supportingDocumentNumber = 'supportingDocumentNumber',
    supportingDocumentDate = 'supportingDocumentDate',
    documentIssuer = 'documentIssuer',
    participantTin = 'participantTin',
    destinationCountry = 'destinationCountry',
    sourceFormation = 'sourceFormation',
    formationProcess = 'formationProcess',
    employeeFullName = 'employeeFullName',
    organizationName = 'organizationName',
}

export interface WithdrawalGridRawRow {
    reportId?: string;
    status: DocumentStatus;
    createdAt: string;
    formationProcess?: string;
    supportingDocumentNumber?: string;
    supportingDocumentDate?: string;
    reason?: string;
    codesCount?: number;
    documentIssuer?: string;
    participantTin?: string;
    destinationCountry?: string;
    sourceFormation?: string;
    employeeFullName?: string;
    organizationName?: string;
}

export type WithdrawalGridFormattedRow = Record<WithdrawalAccessors, ReactNode>;

export enum BlockingAccessors {
    id = 'reportId',
    createdDate = 'createdAt',
    formationProcess = 'formationProcess',
    reason = 'reason',
    participantTin = 'participantTin',
    organizationName = 'organizationName',
    codesCount = 'codesCount',
    status = 'status',
}

export type BlockingGridFormattedRow = Record<BlockingAccessors, ReactNode>;

export interface WithdrawalDictionary {
    status?: string[];
    reason?: string[];
    documentIssuer?: string[];
    participantTin?: string[];
    destinationCountry?: string[];
    sourceFormation?: string[];
    formationProcess?: string[];
}

export enum GdsAccessors {
    id = 'id',
    customsGdNumber = 'customsGdNumber',
    customsCode = 'customsCode',
    dateOfReceipt = 'receiptedAt',
    status = 'status',
}

export interface GdsGridRawRow {
    id: string;
    customsGdNumber: string;
    customsCode: string;
    receiptedAt: string;
    participantTin: string;
    participantName: string;
    status: DocumentStatus;
}

export interface GdsGridFormattedRow {
    [GdsAccessors.id]: string;
    [GdsAccessors.customsGdNumber]: string;
    [GdsAccessors.customsCode]: string;
    [GdsAccessors.dateOfReceipt]: string;
    [GdsAccessors.status]: string;
}
export enum ChecksAccessors {
    id = 'receiptId',
    createdDate = 'createdAt',
    checkType = 'operationType',
    seller = 'manufacturerName',
    status = 'status',
}

export interface ChecksGridRawRow {
    receiptId: string;
    createdAt: string;
    operationType: string;
    manufacturerName: string;
    status: string;
}

export interface ReceiptDetailRaw {
    createdAt: string;
    receiptId: string;
    user: string;
    retailAddress: string;
    userInn: string;
    taxationType: number;
    operator: string;
    shiftNumber: number;
    dateTime: string;
    fiscalDocumentNumber: number;
    fiscalDriveNumber: string;
    productsTotalCount: number;
    status: CheckStatus;
    errors?: ReceiptDetailError[];
}

export interface ReceiptDetail {
    createdAt: string;
    receiptId: string;
    user: string;
    retailAddress: string;
    userInn: string;
    taxationType: number;
    operator: string;
    shiftNumber: number;
    dateTime: string;
    fiscalDocumentNumber: number;
    fiscalDriveNumber: string;
    productsTotalCount: number;
    status: string;
    errors?: string[];
}

export interface ReceiptDetailError {
    code: string;
    message: string;
}

export interface ChecksGridFormattedRow {
    [ChecksAccessors.id]: ReactNode;
    [ChecksAccessors.createdDate]: string;
    [ChecksAccessors.checkType]: string;
    [ChecksAccessors.seller]: string;
    [ChecksAccessors.status]: string;
}

export enum DocumentsDropOutProductsAssessors {
    code = 'code',
    gtin = 'gtin',
    productName = 'productName',
    productGroupCode = 'productGroupCode',
}
export interface DropOutFormData {
    reason: string;
    salesChannel: string;
    documentId?: string;
    supportingDocumentNumber?: string;
    supportingDocumentDate?: Date | null;
    comment?: string;
    attachmentFiles?: File[];
    counterpartyId?: string;
    counterpartyName?: string;
    destinationCountry?: string;
    resultMarkingCodes?: string[];
}

export interface DocumentDropOutData extends Omit<DropOutFormData, 'documentId'> {
    codes: Array<string>;
    documentId: string;
}

export interface DropoutDictionaryResponse {
    reasons: string[];
    salesChannels: string[];
    formationProcesses?: string[];
}

export interface ReturnGoodsDictionaryResponse {
    productConditions: PRODUCT_CONDITIONS[];
    disposalReasons: ALLOWED_REASONS[];
    returnGoodsSources: DocumentReturnSource[];
    returnTypes: DocumentReturnType[];
}

export interface AggregationFormData {
    aggregationType: string;
    aggregationNumber: string | null;
    storageLocationId: number | null;
    storageLocationName?: string;
}

export interface DocumentAggregationData extends AggregationFormData {
    codes?: Array<string>;
    file?: File | null;
}
export interface UtilisationFormData {
    batchNumberSource?: 'auto' | 'manual';
    groupMarkingCode?: string | null;
    factoryId: string;
    factoryName?: string;
    productionLineId?: string;
    productionLineName?: string;
    applicationDate: Date | string | null;
    manufacturingDate: Date | string | null;
    storageLocationId: string | null;
    storageLocationName?: string;
    batchNumber?: string;
    batchNumberAutoGeneratedHolder?: string | null;
}

export interface DocumentUtilisationData extends UtilisationFormData {
    codes: Array<string>;
    file?: File | null;
}
export interface CommissioningFormData {
    startCirculationDate: Date | null;
}

export interface DocumentCommissioningData extends CommissioningFormData {
    codes: Array<string>;
}

export interface MovingFormData {
    employeeFullName?: string;
    storageLocationFrom: string;
    storageLocationTo: string;
    storageLocationFromId?: string | null;
    storageLocationToId?: string | null;
    note?: string;
}

export interface DocumentMovingData extends MovingFormData {
    codes: Array<string>;
    file?: File | null;
}

/*
  Document details
*/
export interface DocumentDetailCommon {
    reportId: string;
    status: DocumentStatus;
    createdAt: string;
    productsTotalCount?: number;
    errors?:
        | {
              code: string;
              message: string;
          }[]
        | null;
}
export interface DropOutReport extends DocumentDetailCommon {
    reportType: 'DROPOUT';
    reportInfo: {
        reason: string;
        createdAt: string;
        attachment?: string[];
        operator?: string;
        organization?: string;
        sourceFormation?: string;
        supportingDocumentNumber?: string;
        supportingDocumentDate?: string;
        comment?: string;
        batchNumber?: string;
        salesChannel?: string;
        counterpartyId?: string;
        counterpartyName?: string;
        destinationCountry?: string;
        processingType?: string;
        processingRepackingType?: string;
        resultMarkingCodes?: string[];
    };
}
export interface IntroduceReport extends DocumentDetailCommon {
    reportType: 'INTRODUCE';
    reportInfo: {
        producedAt: string;
        manufacturerName: string;
    };
}
export interface UPDReport extends DocumentDetailCommon {
    reportType: 'UPD';
    reportInfo: {
        processedAt: string;
        buyerLegalName: string;
        sellerLegalName: string;
        currency: string;
    };
}
export interface UtilizationReport extends DocumentDetailCommon {
    reportType: 'UTILISATION';
    reportInfo: {
        createdAt: string;
        factoryName: string;
        productionLineName: string;
        storageLocation?: AggregationStorageLocation;
        batchNumber?: string;
        manufacturingDate: string;
    };
}
export interface MovingReport extends DocumentDetailCommon {
    reportType: 'MOVING';
    reportInfo: {
        employeeFullName: string;
        note?: string;
        storageLocationFrom?: string;
        storageLocationTo?: string;
    };
}

export interface ReturnGoodsReport extends DocumentDetailCommon {
    reportType: 'RETURN_GOODS';
    reportInfo: {
        cashRegisterId?: string;
        comment?: string;
        createdBy: string;
        disposalReason?: ALLOWED_REASONS;
        markingCode: string;
        newCodeStatus?: ProductStatus;
        organization: string;
        pointOfSaleId?: string | number;
        previousCodeStatus?: ProductStatus;
        processedAt?: string;
        productCode: string;
        productCondition: PRODUCT_CONDITIONS;
        productName: string;
        referenceDocumentNumber?: string;
        returnDate: string;
        returnType: DocumentReturnType;
        source: DocumentReturnSource;
        storageLocation?: number | string | null;
        productGroupCode: ProductGroup;
    };
}

export interface RemarkingReport extends DocumentDetailCommon {
    reportType: 'REMARKING';
    reportInfo: {
        remarkingReason?: 'DAMAGED' | 'LOST' | 'MISPRINT' | 'OTHER';
        reason?: 'DAMAGED' | 'LOST' | 'MISPRINT' | 'OTHER';
        comment?: string;
        markingCode?: string;
        gtin?: string;
        productCode?: string;
        productName?: string;
        productGroupCode?: ProductGroup;
        operator?: string;
        createdBy?: string;
        organization?: string;
        source?: string;
        processedAt?: string;
    };
}

export interface BlockingReport extends DocumentDetailCommon {
    reportType: 'BLOCKING';
    reportInfo: {
        attachment: string[] | null;
        comment: string | null;
        formationProcess: BlockingFormationProcess;
        operator: string;
        organization: string;
        organizationTin: string | null;
        reason: BlokcingReason;
        scopes: BlockingScopes[];
        sourceFormation: BlockingSourceFormation;
        supportingDocumentDate: string | null;
        supportingDocumentNumber: string | null;
    };
}

export interface DisaggregationReport extends DocumentDetailCommon {
    reportType: 'DISAGGREGATION';
    reportInfo: {
        disaggregationType: DisaggregationType;
        operator: string;
        organization: string;
        organizationTin: string;
        source: DocumentReturnSource;
    };
}

export interface AggregationNumbers {
    aggregationNumber: string;
    packagingType: string;
}

type Nullable<T> = {
    [P in keyof T]: T[P] | null;
};
export interface StorageLocationAddress {
    address: string;
    city: string;
    country: string;
    gln: string;
    gps: string;
    id: number;
}

export type NullableStorageLocationAddress = Nullable<StorageLocationAddress>;
export interface AggregationStorageLocation {
    address: NullableStorageLocationAddress;
    clientId: number;
    storageName: string;
}
export interface AggregateReport extends DocumentDetailCommon {
    reportType: 'AGGREGATE';
    reportInfo: {
        createdAt: string;
        groupPackageCount: number | string;
        transportPackageCount?: number | string;
        aggregationNumbers?: Array<AggregationNumbers>;
        storageLocation?: AggregationStorageLocation;
    };
}

export type DocumentDetail =
    | DropOutReport
    | IntroduceReport
    | UtilizationReport
    | AggregateReport
    | UPDReport
    | MovingReport
    | ReturnGoodsReport
    | RemarkingReport
    | BlockingReport
    | DisaggregationReport;

export interface GtinDetailRaw {
    templateId: string;
    gtin: string;
    productId: string;
    productName: string;
    productVendorCode: string;
    quantity: number;
}

export enum ProductsUPDAccessors {
    gtin = 'gtin',
    quantity = 'quantity',
    productGroupCode = 'productGroupCode',
    productName = 'productName',
}

export interface ProductsUPDItem {
    gtin: string;
    quantity: string;
    productGroupCode: string;
    productName: string;
}

export interface AggregationParentCode {
    markingCode: string;
    packagingType: PackagingType;
    status: ProductStatus;
    storageLocationId: number | null;
    hasChildren: boolean;
}

export interface AggregationChildrenCode {
    markingCode: string;
    packagingType: PackagingType;
    status: ProductStatus;
    storageLocationId: number | null;
    productName: string | null;
    parentMarkingCode: string | null;
}

export type DocumentCodesCache = Record<string, DocumentsCodesTreeRow[]>;

export type DocumentsCodesTreeRow = {
    identityCode: string;
    gtin: string;
    status: ProductStatus;
    productName: string;
    packagingType: PackageTypes;
};

export enum ALLOWED_REASONS {
    RETURN_FROM_CONSUMER = 'RETURN_FROM_CONSUMER',
    EXPIRATION = 'EXPIRATION',
    SPOILAGE = 'SPOILAGE',
    PACKAGING_DAMAGE = 'PACKAGING_DAMAGE',
    DESTRUCTION = 'DESTRUCTION',
    PRODUCT_RECALL = 'PRODUCT_RECALL',
}

export type AllowancesByProductGroup = {
    consumerReturnAllowed: boolean;
    reintroductionAllowed: boolean;
    remarkingAllowed: boolean;
    documentsRequared: boolean;
    allowedReasons: ALLOWED_REASONS[];
};

export enum PRODUCT_CONDITIONS {
    FIT_FOR_SALE = 'FIT_FOR_SALE',
    NOT_FIT_FOR_SALE = 'NOT_FIT_FOR_SALE',
}

export type ProductCondition = typeof PRODUCT_CONDITIONS[keyof typeof PRODUCT_CONDITIONS];

export type ValidationContext = {
    reintroductionAllowed: boolean;
};

export interface GeneralInfoData {
    markingCode: string;
    returnDate: Date;
    referenceDocNumber: string;
    productCondition: PRODUCT_CONDITIONS | null;
    comment: string;
    storageLocation: number | null;
    disposalReason?: ALLOWED_REASONS | null;
}
export interface DocumentReturnGoodsData {
    markingCode: string;
    productName?: string;
    productCode?: string;
    productCondition?: PRODUCT_CONDITIONS;
    storageLocation?: number;
    disposalReason?: ALLOWED_REASONS;
    returnDate?: string;
    referenceDocumentNumber?: string;
    comment?: string;
}
